import React, { useState, useEffect } from "react";
import {
  fetchUnknownConversations,
  Conversation,
  downloadAudioFile,
  deleteConversation,
  togglePinConversation,
} from "../../services/api";
import {
  HiOutlinePhone,
  HiOutlineTrash,
  HiPlay,
  HiDownload,
  HiStar,
  HiOutlineStar,
  HiClipboardCheck,
} from "react-icons/hi";
import LoadingState from "../Common/LoadingState";
import ConfirmationModal from "../Common/ConfirmationModal";
import StereoAudioVisualizer from "../Common/AudioVisualizer/AudioVisualizer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlay,
  faPause,
  faTimes,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import "./Admin.css";

interface GroupedConversations {
  [callNumber: string]: Conversation[];
}

const UnknownConversations: React.FC = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [groupedConversations, setGroupedConversations] =
    useState<GroupedConversations>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedPhoneNumber, setExpandedPhoneNumber] = useState<string | null>(
    null
  );
  const [expandedConversationId, setExpandedConversationId] = useState<
    number | null
  >(null);

  // Audio playback states
  const [selectedConversation, setSelectedConversation] =
    useState<Conversation | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [audioLoaded, setAudioLoaded] = useState<boolean>(false);
  const [audioLoading, setAudioLoading] = useState<boolean>(false);
  const [audioError, setAudioError] = useState<string | null>(null);

  // Confirmation modal states
  const [showDeleteConversationModal, setShowDeleteConversationModal] =
    useState<boolean>(false);
  const [conversationToDelete, setConversationToDelete] = useState<string>("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchUnknownConversations();
      setConversations(data);

      // Group conversations by call_number
      const grouped: GroupedConversations = {};
      data.forEach((conv) => {
        if (conv.call_number) {
          if (!grouped[conv.call_number]) {
            grouped[conv.call_number] = [];
          }
          grouped[conv.call_number].push(conv);
        }
      });

      // Sort the conversations in each group by timestamp (newest first)
      Object.keys(grouped).forEach((phone) => {
        grouped[phone].sort(
          (a, b) =>
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        );
      });

      setGroupedConversations(grouped);
    } catch (err: any) {
      console.error("Error loading unknown conversations:", err);
      setError(err.message || "Failed to load unknown conversations");
    } finally {
      setLoading(false);
    }
  };

  // Format date time
  const formatDateTime = (dateTime: string) => {
    const date = new Date(dateTime);
    return date.toLocaleString();
  };

  // Toggle phone number expansion
  const togglePhoneNumberExpand = (phoneNumber: string) => {
    if (expandedPhoneNumber === phoneNumber) {
      setExpandedPhoneNumber(null);
    } else {
      setExpandedPhoneNumber(phoneNumber);
    }
    setExpandedConversationId(null);
  };

  // Toggle conversation expansion
  const toggleConversationExpand = (id: number) => {
    if (expandedConversationId === id) {
      setExpandedConversationId(null);
    } else {
      setExpandedConversationId(id);
    }
  };

  // Handle playing audio recording
  const handlePlay = async (conversation: Conversation) => {
    if (!conversation.recording_filename) {
      setError("No recording available for this conversation");
      return;
    }

    try {
      // Reset error state
      setAudioError(null);

      if (selectedConversation?.id === conversation.id && audioLoaded) {
        setIsPlaying(!isPlaying);
        setShowModal(true);
        return;
      }

      setIsPlaying(false);
      setSelectedConversation(conversation);
      setShowModal(true);
      setAudioLoading(true);

      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
        setAudioUrl(null);
      }

      if (!audioLoaded || selectedConversation?.id !== conversation.id) {
        const audioBlob = await downloadAudioFile(
          conversation.recording_filename
        );

        if (!audioBlob) {
          throw new Error(
            "Failed to download audio file - received empty response"
          );
        }

        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);
        setAudioLoaded(true);
      }

      setAudioLoading(false);

      setTimeout(() => {
        setIsPlaying(true);
      }, 100);
    } catch (err: any) {
      console.error("Error loading audio:", err);
      setAudioError(
        err.message ||
          "Unable to load audio file. The file may be missing or corrupted."
      );
      setAudioLoading(false);
      setAudioLoaded(false);
      setAudioUrl(null);
    }
  };

  // Handle download button click
  const handleDownload = async (conversation: Conversation) => {
    if (!conversation.recording_filename) {
      setError("No recording available for this conversation");
      return;
    }

    try {
      setError(null);
      const audioBlob = await downloadAudioFile(
        conversation.recording_filename
      );

      if (!audioBlob) {
        throw new Error(
          "Failed to download audio file - received empty response"
        );
      }

      const url = URL.createObjectURL(audioBlob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", conversation.recording_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error("Error downloading audio:", err);
      setError(
        err.message ||
          "Unable to download audio file. The file may be missing or corrupted."
      );
    }
  };

  // Toggle play state in modal
  const togglePlayState = () => {
    setIsPlaying(!isPlaying);
  };

  // Close audio modal
  const closeModal = () => {
    setIsPlaying(false);
    setShowModal(false);

    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
    setAudioLoaded(false);
    setAudioError(null);
  };

  // Handle audio ended event
  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  // Handle pin conversation
  const handlePinConversation = async (
    conversationId: string,
    isPinned: boolean
  ) => {
    try {
      await togglePinConversation(conversationId);
      fetchData();
    } catch (err: any) {
      setError(
        `Failed to ${isPinned ? "unpin" : "pin"} conversation: ${err.message}`
      );
    }
  };

  // Open delete conversation modal
  const openDeleteConversationModal = (conversationId: string) => {
    setConversationToDelete(conversationId);
    setShowDeleteConversationModal(true);
  };

  // Handle delete conversation
  const handleDeleteConversation = async () => {
    try {
      await deleteConversation(conversationToDelete);
      fetchData();
      setShowDeleteConversationModal(false);
    } catch (err: any) {
      setError(`Failed to delete conversation: ${err.message}`);
    }
  };

  if (loading && conversations.length === 0) {
    return (
      <div className="container mx-auto py-8 px-4">
        <LoadingState
          message="Loading unknown conversations..."
          fullPage={true}
        />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-[var(--foreground)]">
          Unknown Calls
        </h1>
        <div className="text-sm text-[var(--foreground-muted)]">
          {Object.keys(groupedConversations).length} phone numbers,{" "}
          {conversations.length} total conversations
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 text-red-600 rounded-md text-sm mb-4">
          {error}
          <button
            className="ml-2 text-red-700 hover:text-red-900"
            onClick={() => setError(null)}
          >
            ✕
          </button>
        </div>
      )}

      <div className="bg-[var(--card-container)] shadow-md rounded-lg p-6">
        <div className="mb-6 flex items-center justify-between">
          <div className="text-sm text-[var(--foreground-muted)]">
            Showing all unknown calls grouped by phone number
          </div>
        </div>

        {Object.keys(groupedConversations).length === 0 ? (
          <div className="text-center py-8">
            <div className="flex flex-col items-center justify-center py-12">
              <img
                src="/assets/no-conversations.png"
                alt="No unknown calls"
                className="w-44 h-auto mb-6"
              />
              <h3 className="text-2xl font-bold text-[var(--foreground)] mb-4">
                No Calls
              </h3>
              <p className="text-[var(--foreground-muted)] text-lg max-w-lg mx-auto mb-8">
                There are currently no unknown calls in the system.
              </p>
              <button
                onClick={() => (window.location.href = "/")}
                className="px-6 py-3 bg-[var(--accent)] text-white rounded-md hover:bg-[var(--accent-hover)] transition-colors font-semibold"
              >
                Back to Home
              </button>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[var(--border)]">
              <thead className="bg-[var(--muted)]">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-[var(--foreground-muted)] uppercase tracking-wider"
                  >
                    Phone Number
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-[var(--foreground-muted)] uppercase tracking-wider"
                  >
                    Conversations
                  </th>
                </tr>
              </thead>
              <tbody className="bg-[var(--card)] divide-y divide-[var(--border)]">
                {Object.entries(groupedConversations).map(
                  ([phoneNumber, convs]) => (
                    <React.Fragment key={phoneNumber}>
                      <tr
                        className="hover:bg-[var(--muted)]/50 cursor-pointer transition-colors"
                        onClick={() => togglePhoneNumberExpand(phoneNumber)}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <HiOutlinePhone className="mr-2 text-[var(--accent)]" />
                            <span className="font-medium text-[var(--foreground)]">
                              {phoneNumber}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm">
                            <span className="font-medium text-[var(--foreground)]">
                              {convs.length} conversations
                            </span>
                            <div className="text-xs text-[var(--foreground-muted)] mt-1">
                              Latest: {formatDateTime(convs[0].timestamp)}
                            </div>
                          </div>
                        </td>
                      </tr>
                      {expandedPhoneNumber === phoneNumber && (
                        <tr>
                          <td
                            colSpan={2}
                            className="px-6 py-4 bg-[var(--card)]"
                          >
                            <div className="rounded-md bg-[var(--card)] p-4 border border-[var(--border)]">
                              <h3 className="text-md font-semibold text-[var(--foreground)] mb-2 flex items-center">
                                <HiClipboardCheck className="mr-2 text-[var(--accent)]" />
                                Conversations for {phoneNumber}
                              </h3>
                              <div className="space-y-3 max-h-96 overflow-y-auto">
                                {convs.map((conv) => (
                                  <div
                                    key={conv.id}
                                    className={`bg-[var(--card-container)] rounded-md shadow-sm border ${
                                      conv.pinned
                                        ? "border-[var(--accent)] shadow-md"
                                        : "border-[var(--border)]"
                                    } hover:shadow-md transition-all duration-300`}
                                  >
                                    <div
                                      className="p-3 cursor-pointer flex justify-between items-center hover:bg-[var(--background)]/50 transition-colors"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (conv.id)
                                          toggleConversationExpand(conv.id);
                                      }}
                                    >
                                      <div>
                                        <div className="flex items-center">
                                          {conv.pinned && (
                                            <span
                                              className="mr-2 text-[var(--accent)]"
                                              title="Pinned conversation"
                                            >
                                              <HiStar className="w-4 h-4" />
                                            </span>
                                          )}
                                          <h4 className="font-medium text-[var(--foreground)]">
                                            {conv.agent_name ||
                                              `Agent #${conv.agent_id}`}
                                          </h4>
                                        </div>
                                        <p className="text-xs text-[var(--foreground-muted)]">
                                          {formatDateTime(conv.timestamp)}
                                        </p>
                                        <p className="text-xs text-[var(--foreground-muted)]">
                                          Phone: {conv.call_number}
                                        </p>
                                      </div>
                                      <div className="flex items-center space-x-2">
                                        {conv.recording_filename && (
                                          <>
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handlePlay(conv);
                                              }}
                                              className="text-[var(--foreground-muted)] hover:text-[var(--accent)] bg-[var(--muted)] rounded-full w-8 h-8 flex items-center justify-center hover:bg-[var(--background)] transition-colors border border-[var(--border)]"
                                              title="Play recording"
                                            >
                                              <HiPlay className="w-4 h-4" />
                                            </button>

                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleDownload(conv);
                                              }}
                                              className="text-[var(--foreground-muted)] hover:text-[var(--accent)] bg-[var(--muted)] rounded-full w-8 h-8 flex items-center justify-center hover:bg-[var(--background)] transition-colors border border-[var(--border)]"
                                              title="Download recording"
                                            >
                                              <HiDownload className="w-4 h-4" />
                                            </button>
                                          </>
                                        )}

                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (conv.id)
                                              handlePinConversation(
                                                conv.id.toString(),
                                                !!conv.pinned
                                              );
                                          }}
                                          className="text-[var(--foreground-muted)] hover:text-[var(--accent)] bg-[var(--muted)] rounded-full w-8 h-8 flex items-center justify-center hover:bg-[var(--background)] transition-colors border border-[var(--border)]"
                                          title={
                                            conv.pinned
                                              ? "Unpin conversation"
                                              : "Pin conversation"
                                          }
                                        >
                                          {conv.pinned ? (
                                            <HiStar className="w-4 h-4 text-[var(--accent)]" />
                                          ) : (
                                            <HiOutlineStar className="w-4 h-4" />
                                          )}
                                        </button>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (conv.id)
                                              openDeleteConversationModal(
                                                conv.id.toString()
                                              );
                                          }}
                                          className="text-[var(--foreground-muted)] hover:text-[var(--destructive)] bg-[var(--muted)] rounded-full w-8 h-8 flex items-center justify-center hover:bg-[var(--background)] transition-colors border border-[var(--border)]"
                                          title="Delete conversation"
                                        >
                                          <HiOutlineTrash className="w-4 h-4" />
                                        </button>
                                      </div>
                                    </div>

                                    {conv.id &&
                                      expandedConversationId === conv.id &&
                                      conv.chat_history && (
                                        <div className="p-3 pt-0 border-t border-[var(--border)]">
                                          <div className="mt-2 space-y-3 max-h-80 overflow-y-auto p-2">
                                            {conv.chat_history.map((msg, i) => (
                                              <div
                                                key={i}
                                                className={`p-2 rounded-lg max-w-[85%] ${
                                                  msg.role === "assistant"
                                                    ? "chat-message assistant"
                                                    : "chat-message user"
                                                }`}
                                              >
                                                <p className="text-sm">
                                                  {msg.content}
                                                </p>
                                                <p className="message-sender">
                                                  {msg.role === "assistant"
                                                    ? "Agent"
                                                    : "User"}
                                                </p>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal with audio visualizer */}
      {showModal && selectedConversation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[var(--card-container)] rounded-lg shadow-xl max-w-2xl w-full mx-4">
            <div className="p-4 border-b border-[var(--border)] flex justify-between items-center">
              <h2 className="text-lg font-medium text-[var(--foreground)]">
                {selectedConversation.agent_name ||
                  `Agent #${selectedConversation.agent_id}`}
                <span className="text-sm text-[var(--foreground-muted)] ml-2">
                  {formatDateTime(selectedConversation.timestamp)}
                </span>
                <div className="text-sm text-[var(--foreground-muted)]">
                  Phone: {selectedConversation.call_number}
                </div>
              </h2>
              <button
                className="text-[var(--foreground-muted)] hover:text-[var(--foreground)]"
                onClick={closeModal}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="p-6 bg-[var(--card-container)] rounded-b-lg">
              {audioLoading ? (
                <div className="flex items-center justify-center py-10">
                  <LoadingState
                    size="medium"
                    padding="none"
                    message="Loading audio..."
                    fullPage={false}
                  />
                </div>
              ) : audioError ? (
                <div className="text-center py-8">
                  <p className="text-[var(--destructive)]">{audioError}</p>
                </div>
              ) : (
                audioUrl && (
                  <StereoAudioVisualizer
                    audioUrl={audioUrl}
                    isPlaying={isPlaying}
                    onEnded={handleAudioEnded}
                  />
                )
              )}

              <div className="flex justify-center items-center space-x-4 mt-6">
                <button
                  className="rounded-full w-12 h-12 flex items-center justify-center bg-[var(--accent)] text-[var(--accent-foreground)] hover:opacity-90 transition-colors"
                  onClick={togglePlayState}
                  disabled={loading || !audioUrl || Boolean(audioError)}
                >
                  <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
                </button>

                <button
                  className="rounded-full w-12 h-12 flex items-center justify-center bg-[var(--accent)] text-[var(--accent-foreground)] hover:opacity-90 transition-colors ml-4"
                  onClick={() =>
                    selectedConversation && handleDownload(selectedConversation)
                  }
                  disabled={loading}
                >
                  <FontAwesomeIcon icon={faDownload} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Conversation Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConversationModal}
        title="Delete Conversation"
        message="Are you sure you want to delete this conversation? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteConversation}
        onCancel={() => setShowDeleteConversationModal(false)}
        isDestructive={true}
      />
    </div>
  );
};

export default UnknownConversations;
